import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n";

export default function VehicleCard({ vehicle, pricing, reservePath = "/reserver" }) {
  const location = useLocation();
  const { t } = useTranslation();
  const match = location.pathname.match(/^\/(en|de|it|es|pt)(\/|$)/);
  const langPrefix = match ? `/${match[1]}` : "";
  const finalReservePath = `${langPrefix}${reservePath}`;

  return (
    <article className="bg-white border rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition">
      <div className="p-6 grid lg:grid-cols-[320px_1fr_260px] gap-6 items-start">
        {/* LEFT: title + image */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {vehicle.titleKey ? t(vehicle.titleKey) : vehicle.title}
          </h3>
          {(vehicle.subtitleKey || vehicle.subtitle) && (
            <p className="text-sm text-gray-600 mt-1">
              {vehicle.subtitleKey ? t(vehicle.subtitleKey) : vehicle.subtitle}
            </p>
          )}

          <div className="mt-4 bg-gray-50 border rounded-xl p-3">
            <img
              src={vehicle.image}
              alt={vehicle.titleKey ? t(vehicle.titleKey) : vehicle.title}
              className="w-full h-44 object-contain"
            />
          </div>
        </div>

        {/* MIDDLE: includes + description */}
        <div>
          <div className="text-xs font-semibold text-gray-800 tracking-wide mb-3">
            {t("vehicle.includes")}
          </div>

          <ul className="space-y-2 text-sm text-gray-700">
            {vehicle.includesKeys ? (
              vehicle.includesKeys.map((key, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-emerald-600">✓</span>
                  <span>{t(key)}</span>
                </li>
              ))
            ) : (
              (vehicle.includes || []).map((item, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-emerald-600">✓</span>
                  <span>{item}</span>
                </li>
              ))
            )}
          </ul>

          {(vehicle.descriptionKey || vehicle.description) && (
            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              {vehicle.descriptionKey ? t(vehicle.descriptionKey) : vehicle.description}
            </p>
          )}
        </div>

        {/* RIGHT: total + CTA */}
        <div className="border rounded-xl p-4 bg-gray-50">
          <div className="text-xs font-semibold text-gray-800 mb-2">
            {t("vehicle.total")}
          </div>

          {pricing?.valid ? (
            <>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {pricing.total} CHF
              </div>

              <div className="text-xs text-gray-600 space-y-1 mb-4">
                <div>{t("vehicle.week")} : {pricing.weekHours} {t("vehicle.hours")}</div>
                <div>{t("vehicle.weekend")} : {pricing.weekendHours} {t("vehicle.hours")}</div>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-600 py-8 text-center mb-4">
              {t("vehicle.noDates")}
            </div>
          )}

          <Link
            to={finalReservePath}
            className="block w-full text-center rounded-lg bg-emerald-600 px-4 py-3 text-white font-semibold hover:bg-emerald-700 transition"
          >
            {t("vehicle.book")}
          </Link>

          <p className="mt-3 text-[11px] text-center text-gray-500">
            {t("vehicle.priceInfo")}
          </p>
        </div>
      </div>

      {/* BOTTOM SPECS BAR */}
      <div className="border-t bg-white px-6 py-4">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700">
          <Spec label={`${vehicle.seats} ${t("vehicle.seats")}`} icon="👥" />
          <Spec label={vehicle.transmission === "Manual" ? t("vehicle.manual") : t("vehicle.automatic")} icon="⚙️" />
          <Spec label={vehicle.fuel === "Diesel" ? t("vehicle.diesel") : t("vehicle.petrol")} icon="⛽" />
          <Spec label={vehicle.ac ? t("vehicle.ac") : t("vehicle.noAc")} icon="❄️" />
          <Spec label={`${vehicle.luggage} ${t("vehicle.luggage")}`} icon="🧳" />
        </div>
      </div>
    </article>
  );
}

function Spec({ icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <span>{icon}</span>
      <span>{label}</span>
    </div>
  );
}